<template>
  <Teleport v-if="show" to="body">
    <div
      class="modal-backdrop fixed inset-0 z-50 flex items-center justify-center light-theme"
      data-testid="visitor-modal-backdrop"
      @click.self="handleClose"
    >
      <div class="my-8 flex max-h-[95vh] w-11/12 max-w-5xl flex-col rounded-lg bg-surface shadow-xl" data-testid="visitor-modal">
        <!-- Header (fixed) -->
        <div class="flex flex-shrink-0 items-center justify-between rounded-t-lg bg-surface-inverse px-4 py-3 text-inverse">
          <div class="flex items-center justify-start gap-4">
            <h2 class="text-body-m-bold text-disabled">{{ visitor.profile.name }}</h2>

            <DropDownSelect dark>
              <template #activator="{ open }">
                <button
                  :class="visitorStatusColorClass(currentStatus)"
                  class="visitor-status-popup flex items-center gap-1 p-2"
                  data-testid="visitor-modal-status"
                  @click.stop.prevent="open"
                >
                  {{ t(`VisitorProfiles.profileCard.status.${currentStatus}`) }}
                  <MdiSvg :path="mdiChevronDown" :size="24" />
                </button>
              </template>

              <template #default="{ close }">
                <ul class="flex flex-col gap-4 border-true-white/10 px-3 py-2" data-testid="visitor-modal-options" @click.stop>
                  <li v-for="option in options" :key="option.value" class="flex items-center gap-4">
                    <button
                      :class="visitorStatusColorClass(option.value)"
                      class="visitor-status-popup px-2 py-1 text-nowrap"
                      :data-testid="`visitor-modal-option-${option.value}`"
                      @click.stop.prevent="(handleStatusChange(option.value), close())"
                    >
                      {{ option.text }}
                    </button>
                    <MdiSvg v-if="currentStatus === option.value" :path="mdiCheck" :size="18" class="text-subtle" />
                  </li>
                </ul>
              </template>
            </DropDownSelect>
          </div>
          <button
            class="text-inverse hover:text-inverse focus:outline-none"
            aria-label="Close"
            data-testid="visitor-modal-close"
            @click="handleClose"
          >
            <XIcon class="lucide-icon-m" />
          </button>
        </div>

        <!-- Content (scrollable) with borders -->
        <div class="modal-frame scrollbar-thin flex-grow overflow-y-auto rounded-b-lg">
          <VisitorProfileDetails :visitor="visitor.profile" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { mdiChevronDown, mdiCheck } from '@mdi/js'
import { MdiSvg } from '@ramp106/omrjs-core-ui'
import { XIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { VisitorProfileStatusEnum, useUpdateVisitorProfileStatusMutation } from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'
import type { EnsureAllKeys } from '@/types'
import DropDownSelect from '@/ui/DropDownSelect.vue'

import { ROOT_EVENT_SLUG } from './config'
import type { CompanyVisitorProfile } from './types'
import VisitorProfileDetails from './VisitorProfileDetails.vue'

const STATUS_ORDER: EnsureAllKeys<VisitorProfileStatusEnum> = [
  VisitorProfileStatusEnum.Unprocessed,
  VisitorProfileStatusEnum.Interesting,
  VisitorProfileStatusEnum.Contacted,
  VisitorProfileStatusEnum.NotRelevant,
]

const props = defineProps<{
  show: boolean
  visitor: CompanyVisitorProfile
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { t } = useI18n()

const currentStatus = ref(props.visitor.status)

watch(
  () => props.visitor.status,
  (newStatus) => {
    currentStatus.value = newStatus
  },
)

const { mutate: updateVisitorProfileStatus, onError, onDone } = useUpdateVisitorProfileStatusMutation()

const options = computed(() => {
  return STATUS_ORDER.map((visitorStatus) => ({
    value: visitorStatus as VisitorProfileStatusEnum,
    text: t(`VisitorProfiles.profileCard.status.${visitorStatus}`),
  }))
})

function visitorStatusColorClass(status: VisitorProfileStatusEnum) {
  return `visitor-status-${status.toLowerCase()}`
}

async function handleStatusChange(value: VisitorProfileStatusEnum) {
  const result = await updateVisitorProfileStatus({
    id: props.visitor.id,
    status: value,
    eventSlug: ROOT_EVENT_SLUG,
  })

  if (result?.data?.updateVisitorProfileStatus?.errors?.length) {
    result.data.updateVisitorProfileStatus.errors.forEach((error) => {
      if (error) showNotification(error, 'error')
    })
    return
  }

  currentStatus.value = value
}

onError((error) => {
  showNotification(error.message, 'error')
})

onDone(() => {
  showNotification(t('VisitorProfiles.profileCard.statusChanged'), 'success')
})

function handleEscapeKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.show) {
    handleClose()
  }
}

function handleClose() {
  document.body.classList.remove('body-scroll-lock')
  document.removeEventListener('keydown', handleEscapeKey)
  emit('close')
}

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      document.body.classList.add('body-scroll-lock')
      document.addEventListener('keydown', handleEscapeKey)
    }
  },
)

onUnmounted(() => {
  if (props.show) {
    document.body.classList.remove('body-scroll-lock')
  }
  document.removeEventListener('keydown', handleEscapeKey)
})
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

@utility visitor-status {
  @apply flex items-center gap-1 rounded-sm border text-[14px] leading-[1.5] font-bold not-italic;
}

.visitor-status-popup.visitor-status-unprocessed {
  @apply visitor-status;
  @apply border-purple-600/80 bg-primary-stronger text-primary-subtle;
}

.visitor-status-popup.visitor-status-interesting {
  @apply visitor-status;
  @apply border-yellow-700 bg-secondary-strong text-warning-inverse;
}

.visitor-status-popup.visitor-status-contacted {
  @apply visitor-status;
  @apply border-jade-700 bg-jade-900 text-jade-200;
}

.visitor-status-popup.visitor-status-not_relevant {
  @apply visitor-status;
  @apply border-true-white/5 bg-surface-inverse text-disabled;
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  overflow: hidden !important;
}

.modal-frame {
  border-bottom-width: calc(var(--spacing) * 2.5);
  border-left-width: calc(var(--spacing) * 2.5);
  border-color: var(--background-color-surface-inverse);
}

.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: var(--background-color-button-primary-neutral);
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background-color: var(--background-color-scrollbar-handle);
  border-radius: var(--radius-md);
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: var(--background-color-scrollbar-handle) var(--background-color-button-primary-neutral);
}
</style>
